import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { message, Row } from "antd";
import { INPUT_SIZE } from "../../../common/ThemeConstants";
import BasicModalFormComponent from "../../../components/formComponent/BasicModalFormComponent";
import FormTextField from "../../../components/general/FormTextField";
import FormSelect from "../../../components/general/FormSelect";
import FormCheckbox from "../../../components/general/FormCheckbox";
import ModalComponent from "../../../components/formComponent/ModalComponent";
import { postRequest } from "../../../services/mainApp.service";

const initialFormValues = {
  ItemId: null,
  ItemName: "",
  SubCategoryId: null,
  PurchaseUnitId: null,
  IssuanceUnitId: null,
  RecipeUnitId: null,
  PurchaseIssuanceConversion: 1,
  IssuanceRecipeConversion: 1,
  IsEnable: true,
  CategoryId: null,
  IsSubRecipe: false,
  ItemCode: "",
  Barcode: "",
  MinParLevel: null,
  MaxParLevel: null,
  IsSameParLevel: 1,
};

const initialSearchValues = {
  SearchText: "",
};

const columns = [
  { title: "Item Name", dataIndex: "ItemName", key: "ItemName" },
  { title: "Sub Category", dataIndex: "SubCategoryName", key: "SubCategoryName" },
  { title: "Category", dataIndex: "CategoryName", key: "CategoryName" },
  { title: "Purchase Unit", dataIndex: "PurchaseUnit", key: "PurchaseUnit" },
  { title: "Issuance Unit", dataIndex: "IssuanceUnit", key: "IssuanceUnit" },
  { title: "Recipe Unit", dataIndex: "RecipeUnit", key: "RecipeUnit" },
];

const InventoryItem = () => {
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);

  const [formFields, setFormFields] = useState(initialFormValues);
  const [searchFields, setSearchFields] = useState(initialSearchValues);
  const [itemList, setItemList] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [supportingTable, setSupportingTable] = useState({});
  const [updateId, setUpdateId] = useState(null);
  const [shouldCloseModal, setShouldCloseModal] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [addCategoryModalVisible, setAddCategoryModalVisible] = useState(false);
  const [addCategoryName, setAddCategoryName] = useState("");
  const [addCategoryLoading, setAddCategoryLoading] = useState(false);
  const [addPurchaseUnitModalVisible, setAddPurchaseUnitModalVisible] = useState(false);
  const [addPurchaseUnitName, setAddPurchaseUnitName] = useState("");
  const [addPurchaseUnitLoading, setAddPurchaseUnitLoading] = useState(false);
  const [addIssuanceUnitModalVisible, setAddIssuanceUnitModalVisible] = useState(false);
  const [addIssuanceUnitName, setAddIssuanceUnitName] = useState("");
  const [addIssuanceUnitLoading, setAddIssuanceUnitLoading] = useState(false);
  const [addRecipeUnitModalVisible, setAddRecipeUnitModalVisible] = useState(false);
  const [addRecipeUnitName, setAddRecipeUnitName] = useState("");
  const [addRecipeUnitLoading, setAddRecipeUnitLoading] = useState(false);
  const [addSubCategoryModalVisible, setAddSubCategoryModalVisible] = useState(false);
  const [addSubCategoryName, setAddSubCategoryName] = useState("");
  const [addSubCategoryLoading, setAddSubCategoryLoading] = useState(false);

  const fetchData = (searchParams = {}, pageNumber = 1, pageSize = 10) => {
    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      ItemId: null,
      SearchText: searchParams.SearchText || "",
      PageNumber: pageNumber,
      PageSize: pageSize,
    };

    postRequest("/Inv_Item", payload, controller)
      .then((response) => {
        setTableLoading(false);
        if (response.error) {
          message.error(response.errorMessage);
          return;
        }
        if (response.data.response === false) {
          message.error(response.data.DataSet?.Table?.errorMessage || "Error fetching data");
          return;
        }

        const dataSet = response.data;
        setItemList(dataSet.Data || []);
        setPagination((prev) => ({
          ...prev,
          current: pageNumber,
          pageSize: pageSize,
          total: dataSet?.TotalCount || dataSet?.TotalRecords || dataSet?.Data?.length || 0,
        }));

        if (dataSet.AdditionalData) {
          setSupportingTable({ AdditionalData: dataSet.AdditionalData });
        }
      })
      .catch((error) => {
        setTableLoading(false);
        if (error.message !== "canceled") {
          console.error("Error fetching items:", error);
          message.error("Failed to fetch items");
        }
      });
  };

  useEffect(() => {
    fetchData({}, pagination.current, pagination.pageSize);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (updateId === null) return;

    const selectedItem = itemList.find((item) => item.ItemId === updateId);
    if (!selectedItem) {
      setUpdateId(null);
      return;
    }

    const { PurchaseUnitId, IssuanceUnitId, RecipeUnitId, PurchaseIssuanceConversion, IssuanceRecipeConversion } = selectedItem;
    const purchaseIssuanceSame = PurchaseUnitId && IssuanceUnitId && PurchaseUnitId === IssuanceUnitId;
    const issuanceRecipeSame = IssuanceUnitId && RecipeUnitId && IssuanceUnitId === RecipeUnitId;

    setFormFields({
      ...selectedItem,
      PurchaseUnitId: PurchaseUnitId || null,
      IssuanceUnitId: IssuanceUnitId || null,
      RecipeUnitId: RecipeUnitId || null,
      PurchaseIssuanceConversion: purchaseIssuanceSame ? 1 : (PurchaseIssuanceConversion ?? 1),
      IssuanceRecipeConversion: issuanceRecipeSame ? 1 : (IssuanceRecipeConversion ?? 1),
      IsEnable: selectedItem.IsEnable ?? true,
      IsSubRecipe: selectedItem.IsSubRecipe ?? false,
    });
    setUpdateId(null);
  }, [updateId, itemList]);

  const handleFormChange = (data) => {
    setFormFields((prev) => {
      const updated = { ...prev, [data.name]: data.value };

      // Reset SubCategoryId when CategoryId changes
      if (data.name === "CategoryId") {
        updated.SubCategoryId = null;
      }

      // Auto-set conversion to 1 when units match
      if (data.name === "PurchaseUnitId" || data.name === "IssuanceUnitId") {
        if (updated.PurchaseUnitId && updated.IssuanceUnitId && updated.PurchaseUnitId === updated.IssuanceUnitId) {
          updated.PurchaseIssuanceConversion = 1;
        }
      }

      if (data.name === "IssuanceUnitId" || data.name === "RecipeUnitId") {
        if (updated.IssuanceUnitId && updated.RecipeUnitId && updated.IssuanceUnitId === updated.RecipeUnitId) {
          updated.IssuanceRecipeConversion = 1;
        }
      }

      return updated;
    });
  };

  const handleSearchChange = (data) => {
    setSearchFields((prev) => ({ ...prev, [data.name]: data.value }));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmedSearch = { ...searchFields, SearchText: searchFields.SearchText?.trim() || "" };
    setSearchFields(trimmedSearch);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(trimmedSearch, 1, pagination.pageSize);
  };

  const handleDeleteRow = (id) => {
    const payload = {
      OperationId: 4,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      ItemId: id,
    };

    postRequest("/Inv_Item", payload, controller)
      .then((response) => {
        if (response.error) {
          message.error(response.errorMessage);
          return;
        }
        if (response.data.response === false) {
          message.error(response.data.DataSet?.Table?.errorMessage || "Error deleting item");
          return;
        }
        message.success(response.successMessage || "Item deleted successfully");
        fetchData(searchFields, pagination.current, pagination.pageSize);
      })
      .catch((error) => {
        if (error.message !== "canceled") {
          console.error("Error deleting item:", error);
          message.error("Failed to delete item");
        }
      });
  };

  const handleFormSubmit = (e, operationId) => {
    e.preventDefault();

    // Validation
    if (!formFields.ItemName?.trim()) {
      message.error("Please enter Item Name");
      return;
    }
    if (!formFields.SubCategoryId) {
      message.error("Please select Sub Category");
      return;
    }
    if (!formFields.PurchaseUnitId) {
      message.error("Please select Purchase Unit");
      return;
    }
    if (!formFields.IssuanceUnitId) {
      message.error("Please select Issuance Unit");
      return;
    }
    if (!formFields.RecipeUnitId) {
      message.error("Please select Recipe Unit");
      return;
    }
    if (!formFields.PurchaseIssuanceConversion || formFields.PurchaseIssuanceConversion <= 0) {
      message.error("Please enter valid Purchase Issuance Conversion");
      return;
    }
    if (!formFields.IssuanceRecipeConversion || formFields.IssuanceRecipeConversion <= 0) {
      message.error("Please enter valid Issuance Recipe Conversion");
      return;
    }
    if (!formFields.MinParLevel || formFields.MinParLevel <= 0) {
      message.error("Please enter valid Min Par Level");
      return;
    }
    if (!formFields.MaxParLevel || formFields.MaxParLevel <= 0) {
      message.error("Please enter valid Max Par Level");
      return;
    }

    setFormLoading(true);

    const itemCode = formFields.ItemCode || supportingTable?.AdditionalData?.TransactionCode?.[0]?.Code || "";

    const payload = {
      OperationId: operationId,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      ItemId: formFields.ItemId,
      ItemName: formFields.ItemName,
      SubCategoryId: formFields.SubCategoryId,
      PurchaseUnitId: formFields.PurchaseUnitId,
      IssuanceUnitId: formFields.IssuanceUnitId,
      RecipeUnitId: formFields.RecipeUnitId,
      PurchaseIssuanceConversion: formFields.PurchaseIssuanceConversion || 1,
      IssuanceRecipeConversion: formFields.IssuanceRecipeConversion || 1,
      IsEnable: formFields.IsEnable,
      IsSubRecipe: formFields.IsSubRecipe,
      ItemCode: itemCode,
      Barcode: formFields.Barcode || "",
      MinParLevel: formFields.MinParLevel,
      MaxParLevel: formFields.MaxParLevel,
    };

    postRequest("/Inv_Item", payload, controller)
      .then((response) => {
        setFormLoading(false);
        const dataSet = response?.data;

        if (response.error) {
          message.error(response.errorMessage || response?.data?.Error_Message);
          return;
        }
        if (response?.data?.HasError) {
          message.error(response?.data?.Error_Message || "Error saving item");
          return;
        }

        message.success(response?.data?.Message || "Item saved successfully");
        setItemList(dataSet?.Data || []);

        if (dataSet?.AdditionalData && Object.keys(dataSet.AdditionalData).length > 0) {
          if (operationId !== 1) {
            // Only update TransactionCode
            if (dataSet?.AdditionalData?.TransactionCode) {
              setFormFields((prev) => ({
                ...prev,
                ItemCode: dataSet?.AdditionalData?.TransactionCode[0]?.Code || prev.ItemCode,
              }));
            }
          } else {
            setSupportingTable({ AdditionalData: dataSet.AdditionalData });
          }
        }

        setFormFields(initialFormValues);
        setUpdateId(null);
        if (operationId === 3) setShouldCloseModal(true);
      })
      .catch((error) => {
        setFormLoading(false);
        if (error.message !== "canceled") {
          console.error("Error saving item:", error);
          message.error("Failed to save item");
        }
      });
  };

  const handleFormClose = () => {
    setFormFields(initialFormValues);
    setUpdateId(null);
    setShouldCloseModal(false);
  };

  const handleAddCategoryModalOpen = () => {
    setAddCategoryName("");
    setAddCategoryModalVisible(true);
  };

  const handleAddCategoryModalClose = () => {
    setAddCategoryModalVisible(false);
    setAddCategoryName("");
  };

  const handleAddCategorySubmit = () => {
    if (!addCategoryName?.trim()) {
      message.error("Please enter Category Name");
      return;
    }
    if (addCategoryLoading) return;
    setAddCategoryLoading(true);
    const payload = {
      OperationId: 2,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      CategoryId: null,
      CategoryName: addCategoryName.trim(),
      DepartmentId: null,
      IsEnable: true,
    };

    postRequest("/Inv_Category", payload, controller)
      .then((response) => {
        setAddCategoryLoading(false);
        if (response.error) {
          message.error(response.errorMessage || response?.data?.Error_Message);
          return;
        }
        if (response?.data?.HasError) {
          message.error(response?.data?.Error_Message || "Error saving category");
          return;
        }
        message.success(response?.data?.Message || "Category created successfully");

        const newCategoryId = response?.data?.Id;
        const newCategoryName = addCategoryName.trim();

        if (newCategoryId) {

          setSupportingTable((prev) => {
            const categories = prev?.AdditionalData?.Categories || [];
            const exists = categories.some((c) => (c.Id ?? c.CategoryId) === newCategoryId);
            if (exists) return prev;
            const updatedCategories = [
              ...categories,
              { Id: newCategoryId, Name: newCategoryName, ParentId: null },
            ];
            return {
              ...prev,
              AdditionalData: {
                ...prev?.AdditionalData,
                Categories: updatedCategories,
              },
            };
          });

          setFormFields((prev) => ({ ...prev, CategoryId: newCategoryId, SubCategoryId: null }));
        } else {
          fetchData({}, pagination.current, pagination.pageSize);
        }

        handleAddCategoryModalClose();
      })
      .catch((error) => {
        setAddCategoryLoading(false);
        if (error.message !== "canceled") {
          console.error("Error creating category:", error);
          message.error("Failed to create category");
        }
      });
  };

  const createUnitAndSelect = (unitName, targetField, setLoading, onClose) => {
    if (!unitName?.trim()) {
      message.error("Please enter Unit Name");
      return;
    }
    if (setLoading) setLoading(true);
    const payload = {
      OperationId: 2,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      UnitId: null,
      UnitName: unitName.trim(),
    };

    postRequest("/CrudUnit", payload, controller)
      .then((response) => {
        if (setLoading) setLoading(false);
        if (response.error) {
          message.error(response.errorMessage || response?.data?.Error_Message);
          return;
        }
        if (response?.data?.HasError) {
          message.error(response?.data?.Error_Message || "Error saving unit");
          return;
        }
        message.success(response?.data?.Message || "Unit created successfully");
        const newUnit = response?.data;
        const newUnitId = newUnit?.UnitId ?? newUnit?.Id;
        const newUnitNameVal = newUnit?.UnitName ?? newUnit?.Name ?? unitName.trim();

        if (newUnitId) {
          setSupportingTable((prev) => {
            const units = prev?.AdditionalData?.Units || [];
            const exists = units.some((u) => (u.Id ?? u.UnitId) === newUnitId);
            if (exists) return prev;
            const updatedUnits = [
              ...units,
              { Id: newUnitId, Name: newUnitNameVal, ParentId: null },
            ];
            return {
              ...prev,
              AdditionalData: {
                ...prev?.AdditionalData,
                Units: updatedUnits,
              },
            };
          });
          setFormFields((prev) => ({ ...prev, [targetField]: newUnitId }));
        } else {
          fetchData({}, pagination.current, pagination.pageSize);
        }

        onClose?.();
      })
      .catch((error) => {
        if (setLoading) setLoading(false);
        if (error.message !== "canceled") {
          console.error("Error creating unit:", error);
          message.error("Failed to create unit");
        }
      });
  };

  const handleAddPurchaseUnitModalOpen = () => {
    setAddPurchaseUnitName("");
    setAddPurchaseUnitModalVisible(true);
  };

  const handleAddPurchaseUnitModalClose = () => {
    setAddPurchaseUnitModalVisible(false);
    setAddPurchaseUnitName("");
  };

  const handleAddPurchaseUnitSubmit = () => {
    if (addPurchaseUnitLoading) return;
    createUnitAndSelect(addPurchaseUnitName, "PurchaseUnitId", setAddPurchaseUnitLoading, handleAddPurchaseUnitModalClose);
  };

  const handleAddIssuanceUnitModalOpen = () => {
    setAddIssuanceUnitName("");
    setAddIssuanceUnitModalVisible(true);
  };

  const handleAddIssuanceUnitModalClose = () => {
    setAddIssuanceUnitModalVisible(false);
    setAddIssuanceUnitName("");
  };

  const handleAddIssuanceUnitSubmit = () => {
    if (addIssuanceUnitLoading) return;
    createUnitAndSelect(addIssuanceUnitName, "IssuanceUnitId", setAddIssuanceUnitLoading, handleAddIssuanceUnitModalClose);
  };

  const handleAddRecipeUnitModalOpen = () => {
    setAddRecipeUnitName("");
    setAddRecipeUnitModalVisible(true);
  };

  const handleAddRecipeUnitModalClose = () => {
    setAddRecipeUnitModalVisible(false);
    setAddRecipeUnitName("");
  };

  const handleAddRecipeUnitSubmit = () => {
    if (addRecipeUnitLoading) return;
    createUnitAndSelect(addRecipeUnitName, "RecipeUnitId", setAddRecipeUnitLoading, handleAddRecipeUnitModalClose);
  };

  const handleAddSubCategoryModalOpen = () => {
    if (!formFields.CategoryId) {
      message.error("Please select Category first");
      return;
    }
    setAddSubCategoryName("");
    setAddSubCategoryModalVisible(true);
  };

  const handleAddSubCategoryModalClose = () => {
    setAddSubCategoryModalVisible(false);
    setAddSubCategoryName("");
  };

  const handleAddSubCategorySubmit = () => {
    if (!addSubCategoryName?.trim()) {
      message.error("Please enter Sub Category Name");
      return;
    }
    if (!formFields.CategoryId) {
      message.error("Please select Category first");
      return;
    }
    if (addSubCategoryLoading) return;
    setAddSubCategoryLoading(true);
    const payload = {
      OperationId: 2,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      SubCategoryId: null,
      SubCategoryName: addSubCategoryName.trim(),
      CategoryId: formFields.CategoryId,
      IsEnable: true,
    };

    postRequest("/Inv_SubCategory", payload, controller)
      .then((response) => {
        setAddSubCategoryLoading(false);
        if (response.error) {
          message.error(response.errorMessage || response?.data?.Error_Message);
          return;
        }
        if (response?.data?.HasError) {
          message.error(response?.data?.Error_Message || "Error saving sub category");
          return;
        }
        message.success(response?.data?.Message || "Sub category created successfully");

        const newSubCategory = response?.data;
        const newSubCategoryId = newSubCategory?.Id;
        const newSubCategoryName = addSubCategoryName.trim();

        if (newSubCategoryId) {
          const parentCategoryId = formFields.CategoryId;
          setSupportingTable((prev) => {
            const subCategories = prev?.AdditionalData?.SubCategories || [];
            const exists = subCategories.some((s) => (s.Id ?? s.SubCategoryId) === newSubCategoryId);
            if (exists) return prev;
            const updatedSubCategories = [
              ...subCategories,
              {
                Id: newSubCategoryId,
                Name: newSubCategoryName,
                SubCategoryId: newSubCategoryId,
                SubCategoryName: newSubCategoryName,
                ParentId: parentCategoryId,
              },
            ];
            return {
              ...prev,
              AdditionalData: {
                ...prev?.AdditionalData,
                SubCategories: updatedSubCategories,
              },
            };
          });
          setFormFields((prev) => ({ ...prev, SubCategoryId: newSubCategoryId }));
        } else {
          fetchData({}, pagination.current, pagination.pageSize);
        }

        handleAddSubCategoryModalClose();
      })
      .catch((error) => {
        setAddSubCategoryLoading(false);
        if (error.message !== "canceled") {
          console.error("Error creating sub category:", error);
          message.error("Failed to create sub category");
        }
      });
  };

  const handleReset = () => {
    setSearchFields(initialSearchValues);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(initialSearchValues, 1, pagination.pageSize);
  };

  const handlePagination = (paginationInfo) => {
    const { current, pageSize } = paginationInfo;
    setPagination((prev) => ({ ...prev, current, pageSize }));
    fetchData(searchFields, current, pageSize);
  };

  const isPurchaseIssuanceSame = formFields.PurchaseUnitId && formFields.IssuanceUnitId && formFields.PurchaseUnitId === formFields.IssuanceUnitId;
  const isIssuanceRecipeSame = formFields.IssuanceUnitId && formFields.RecipeUnitId && formFields.IssuanceUnitId === formFields.RecipeUnitId;

  const searchPanel = (
    <Fragment>
      <FormTextField
        colSpan={4}
        label="Search"
        name="SearchText"
        size={INPUT_SIZE}
        value={searchFields.SearchText}
        onChange={handleSearchChange}
      />
    </Fragment>
  );

  const formPanel = (
    <Fragment>
      <FormTextField
        colSpan={8}
        label="Item Code"
        name="ItemCode"
        size={INPUT_SIZE}
        value={formFields.ItemCode || supportingTable?.AdditionalData?.TransactionCode?.[0]?.Code || ""}
        onChange={handleFormChange}
        type="text"
        disabled={true}
      />
      <FormTextField
        colSpan={8}
        label="Item Name"
        name="ItemName"
        size={INPUT_SIZE}
        value={formFields.ItemName}
        onChange={handleFormChange}
        required={true}
      />
      <div className="ant-col ant-col-8 mt-26">
        <FormCheckbox
          name="IsSubRecipe"
          label="Production Item"
          checked={formFields.IsSubRecipe}
          onChange={handleFormChange}
        />
      </div>
      <FormSelect
        colSpan={8}
        listItem={supportingTable?.AdditionalData?.Units || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="PurchaseUnitId"
        placeholder="Purchase Unit"
        label={
          <>
            Purchase Unit
            <a
              onClick={handleAddPurchaseUnitModalOpen}
              style={{ marginLeft: 8, fontSize: 12, cursor: "pointer" }}
            >
              Add Purchase Unit
            </a>
          </>
        }
        value={formFields.PurchaseUnitId}
        onChange={handleFormChange}
        required={true}
      />
      <FormSelect
        colSpan={8}
        listItem={supportingTable?.AdditionalData?.Units || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="IssuanceUnitId"
        placeholder="Issuance Unit"
        label={
          <>
            Issuance Unit
            <a
              onClick={handleAddIssuanceUnitModalOpen}
              style={{ marginLeft: 8, fontSize: 12, cursor: "pointer" }}
            >
              Add Issuance Unit
            </a>
          </>
        }
        value={formFields.IssuanceUnitId}
        onChange={handleFormChange}
        required={true}
      />
      <FormSelect
        colSpan={8}
        listItem={supportingTable?.AdditionalData?.Units || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="RecipeUnitId"
        placeholder="Recipe Unit"
        label={
          <>
            Recipe Unit
            <a
              onClick={handleAddRecipeUnitModalOpen}
              style={{ marginLeft: 8, fontSize: 12, cursor: "pointer" }}
            >
              Add Recipe Unit
            </a>
          </>
        }
        value={formFields.RecipeUnitId}
        onChange={handleFormChange}
        required={true}
      />
      <FormTextField
        colSpan={8}
        label="Purchase Issuance Conversion"
        name="PurchaseIssuanceConversion"
        size={INPUT_SIZE}
        value={formFields.PurchaseIssuanceConversion}
        onChange={handleFormChange}
        type="text"
        isNumber="true"
        required={true}
        disabled={isPurchaseIssuanceSame}
      />
      <FormTextField
        colSpan={8}
        label="Issuance Recipe Conversion"
        name="IssuanceRecipeConversion"
        size={INPUT_SIZE}
        value={formFields.IssuanceRecipeConversion}
        onChange={handleFormChange}
        type="text"
        isNumber="true"
        required={true}
        disabled={isIssuanceRecipeSame}
      />
      <div className="ant-col ant-col-8 mt-26">
        <FormCheckbox
          name="IsEnable"
          label="Is Enabled"
          checked={formFields.IsEnable}
          onChange={handleFormChange}
        />
      </div>

      <FormTextField
        colSpan={8}
        label="Barcode"
        name="Barcode"
        size={INPUT_SIZE}
        value={formFields.Barcode}
        onChange={handleFormChange}
        type="text"
      />

      {formFields.IsSameParLevel === 1 && formFields.ItemId == null && (
        <>
          <FormTextField
            colSpan={8}
            label="Min Par Level"
            name="MinParLevel"
            size={INPUT_SIZE}
            value={formFields.MinParLevel}
            onChange={handleFormChange}
            type="text"
            isNumber="true"
            required={true}
          />
          <FormTextField
            colSpan={8}
            label="Max Par Level"
            name="MaxParLevel"
            size={INPUT_SIZE}
            value={formFields.MaxParLevel}
            onChange={handleFormChange}
            type="text"
            isNumber="true"
            required={true}
          />
        </>
      )
      }
      <FormSelect
        colSpan={8}
        listItem={supportingTable?.AdditionalData?.Categories || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="CategoryId"
        placeholder="Category"
        label={
          <>
            Category
            <a
              onClick={handleAddCategoryModalOpen}
              style={{ marginLeft: 8, fontSize: 12, cursor: "pointer" }}
            >
              Add Category
            </a>
          </>
        }
        value={formFields.CategoryId}
        onChange={handleFormChange}
      />
      <FormSelect
        colSpan={8}
        listItem={(formFields.CategoryId > 0 ?
          supportingTable?.AdditionalData?.SubCategories.filter(a => a.ParentId === formFields.CategoryId) :
          supportingTable?.AdditionalData?.SubCategories) || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="SubCategoryId"
        placeholder="Sub Category"
        label={
          <>
            Sub Category
            <a
              onClick={handleAddSubCategoryModalOpen}
              style={{ marginLeft: 8, fontSize: 12, cursor: "pointer" }}
            >
              Add Sub Category
            </a>
          </>
        }
        value={formFields.SubCategoryId}
        onChange={handleFormChange}
        required={true}
      />
    </Fragment>
  );

  return (
    <>
      <BasicModalFormComponent
        formTitle="Inventory Item"
        searchPanel={searchPanel}
        formPanel={formPanel}
        searchSubmit={handleSearchSubmit}
        onFormSave={handleFormSubmit}
        onFormClose={handleFormClose}
        tableRows={itemList}
        tableLoading={tableLoading}
        formLoading={formLoading}
        tableColumn={columns}
        deleteRow={handleDeleteRow}
        actionID="ItemId"
        editRow={setUpdateId}
        fields={initialFormValues}
        reset={false}
        handleReset={handleReset}
        crudTitle="Item"
        externalCloseTrigger={shouldCloseModal}
        onPaginationChange={handlePagination}
        paginationConfig={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          pageSizeOptions: ['10', '20', '50', '100'],
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
      <ModalComponent
        title="Add Category"
        isModalVisible={addCategoryModalVisible}
        handleOk={handleAddCategorySubmit}
        handleCancel={handleAddCategoryModalClose}
        width={400}
        okText="Create"
      >
        <Row gutter={16}>
          <FormTextField
            colSpan={24}
            label="Category Name"
            name="CategoryName"
            size={INPUT_SIZE}
            value={addCategoryName}
            onChange={(data) => setAddCategoryName(data.value)}
            required={true}
          />
        </Row>
      </ModalComponent>
      <ModalComponent
        title="Add Purchase Unit"
        isModalVisible={addPurchaseUnitModalVisible}
        handleOk={handleAddPurchaseUnitSubmit}
        handleCancel={handleAddPurchaseUnitModalClose}
        width={400}
        okText="Create"
      >
        <Row gutter={16}>
          <FormTextField
            colSpan={24}
            label="Purchase Unit Name"
            name="UnitName"
            size={INPUT_SIZE}
            value={addPurchaseUnitName}
            onChange={(data) => setAddPurchaseUnitName(data.value)}
            required={true}
          />
        </Row>
      </ModalComponent>
      <ModalComponent
        title="Add Issuance Unit"
        isModalVisible={addIssuanceUnitModalVisible}
        handleOk={handleAddIssuanceUnitSubmit}
        handleCancel={handleAddIssuanceUnitModalClose}
        width={400}
        okText="Create"
      >
        <Row gutter={16}>
          <FormTextField
            colSpan={24}
            label="Issuance Unit Name"
            name="UnitName"
            size={INPUT_SIZE}
            value={addIssuanceUnitName}
            onChange={(data) => setAddIssuanceUnitName(data.value)}
            required={true}
          />
        </Row>
      </ModalComponent>
      <ModalComponent
        title="Add Recipe Unit"
        isModalVisible={addRecipeUnitModalVisible}
        handleOk={handleAddRecipeUnitSubmit}
        handleCancel={handleAddRecipeUnitModalClose}
        width={400}
        okText="Create"
      >
        <Row gutter={16}>
          <FormTextField
            colSpan={24}
            label="Recipe Unit Name"
            name="UnitName"
            size={INPUT_SIZE}
            value={addRecipeUnitName}
            onChange={(data) => setAddRecipeUnitName(data.value)}
            required={true}
          />
        </Row>
      </ModalComponent>
      <ModalComponent
        title="Add Sub Category"
        isModalVisible={addSubCategoryModalVisible}
        handleOk={handleAddSubCategorySubmit}
        handleCancel={handleAddSubCategoryModalClose}
        width={400}
        okText="Create"
      >
        <Row gutter={16}>
          <FormTextField
            colSpan={24}
            label="Sub Category Name"
            name="SubCategoryName"
            size={INPUT_SIZE}
            value={addSubCategoryName}
            onChange={(data) => setAddSubCategoryName(data.value)}
            required={true}
          />
        </Row>
      </ModalComponent>
    </>
  );
};

export default InventoryItem;
